import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { FileWithPath } from "react-dropzone";

// MUI
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Components
import BidWinLossTable from "./BidWinLossTable";
import FormikControl from "../formik/FormikControl";
import DropBox from "../dropzone/DropBox";
import Loader from "../loader/Loader";

// Config
import {
  tenderMaturityOptions,
  tenderOptions,
  tenderReleasedOptions,
  bidSubmittedOptions,
  l1Options,
  availProjectBidStatus,
  yesOrNoStatusOptions,
  projectBidStatusObj,
} from "../../utility/config";

// Hooks
import { useAppSelector, useAppDispatch } from "../../redux/hooks/redux-hooks";

// Utils
import { getDecimalValue, isValidArr } from "../../utility/utils";

// Services
import WinLossService from "../../services/WinLossService";
import BidderDetailService from "../../services/BidderDetailService";
import ProjectService from "../../services/ProjectService";

// Actions
import { fetchProjectBidStatusHistory } from "../../redux/actions/WinLossActions";
import { fetchProjectBidderDetails } from "../../redux/actions/BidderDetailActions";
import {
  fetchProjectsWithStatusForUserId,
  fetchAllProjectsWithStatus,
} from "../../redux/actions/ProjectResponseAction";

// Types
import { WinLossPostModel } from "../../models/WinLossModel";
import TextBox from "../TextBox";

export const rowfields = {
  rank: "",
  bidderName: "",
  evaluatedPrice: "",
  remarks: "",
};

export type ObjType = {
  [key: string]: string;
};

type Props = {
  projectId: number;
  projectStatus: string;
  tenderMaturity: string;
};

// Validation schema for bid rank table
const bidTableValidationSchema = Yup.object().shape({
  rank: Yup.string().required("Required"),
  bidderName: Yup.string().required("Required"),
  evaluatedPrice: Yup.string().required("Required"),
  remarks: Yup.string().required("Required"),
});

const BidStatusSection = ({
  projectId,
  projectStatus,
  tenderMaturity,
}: Props) => {
  const dispatch = useAppDispatch();
  const [attachment, setAttachment] = useState<FileWithPath>();

  const [pbt, setPBT] = useState<string>("0");
  const [oh, setOH] = useState<string>("0");
  const [projectMargin, setProjectMargin] = useState<string>("0");
  const [financeCost, setFinanceCost] = useState<string>("0");
  const [depreciation, setDepreciation] = useState<string>("0");
  const [grossMargin, setGrossMargin] = useState<string>("0");
  const [normalContingency, setNormalContingency] = useState<string>("0");
  const [specialContingency, setSpecialContingency] = useState<string>("0");

  const handlePBTChange = (val: string) => {
    let value = getDecimalValue(val);
    setPBT(!value ? pbt : value);

    // if (!isNaN(Number(value))) {
    //   setPBT(value);
    // } else {
    //   setPBT("0");
    // }
  };
  const handleOHChange = (val: string) => {
    let value = getDecimalValue(val);
    setOH(!value ? oh : value);
    //  const value = parseFloat(val);
    //   if (!isNaN(Number(value))) {
    //     setOH(Number(val).toFixed(1).toString());
    //   } else {
    //     setOH("0");
    //   }
  };

  // useEffect(() => {
  //   // setProjectMargin(Number(pbt) + Number(oh));
  // }, [pbt, oh]);

  const handleProjectMarginChange = (val: string) => {
    let value = getDecimalValue(val);
    setProjectMargin(!value ? projectMargin : value);
    //  const value = parseFloat(val).toFixed(1);
    //   if (!isNaN(Number(value))) {
    //     setProjectMargin(value);
    //   } else {
    //     setProjectMargin("0");
    //   }
  };

  const handleFinanceCostChange = (val: string) => {
    let value = getDecimalValue(val);
    setFinanceCost(!value ? financeCost : value);
    //  const value = parseFloat(val).toFixed(1);
    //   if (!isNaN(Number(value))) {
    //     setFinanceCost(value);
    //   } else {
    //     setFinanceCost("0");
    //   }
  };

  const handleDepreciationChange = (val: string) => {
    let value = getDecimalValue(val);
    setDepreciation(!value ? depreciation : value);
    //  const value = parseFloat(val).toFixed(1);
    //   if (!isNaN(Number(value))) {
    //     setDepreciation(value);
    //   } else {
    //     setDepreciation("0");
    //   }
  };

  // useEffect(() => {
  //   setGrossMargin(projectMargin + financeCost + depreciation);
  // }, [projectMargin, financeCost, depreciation]);

  const handleGrossMarginChange = (val: string) => {
    let value = getDecimalValue(val);
    setGrossMargin(!value ? grossMargin : value);
    // const value = (parseFloat(val).toFixed(1));
    // if (!isNaN(Number(value))) {
    //   setGrossMargin(value);
    // } else {
    //   setGrossMargin("0");
    // }
  };

  const handleNormalContingencyChange = (val: string) => {
    let value = getDecimalValue(val);
    setNormalContingency(!value ? normalContingency : value);
    //  const value = parseFloat(val).toFixed(1);
    //   if (!isNaN(Number(value))) {
    //     setNormalContingency(value);
    //   } else {
    //     setNormalContingency("0");
    //   }
  };

  const handleSpecialContingencyChange = (val: string) => {
    let value = getDecimalValue(val);
    setSpecialContingency(!value ? specialContingency : value);
    //  const value = parseFloat(val).toFixed(1);
    //   if (!isNaN(Number(value))) {
    //     setSpecialContingency(value);
    //   } else {
    //     setSpecialContingency("0");
    //   }
  };

  const projectBidStatusHistory = useAppSelector(
    (state) => state.winloss.bidStatusHistory
  );

  useEffect(() => {
    if (projectBidStatusHistory && projectBidStatusHistory.length !== 0) {
      if (projectBidStatusHistory[0].pbt) {
        setPBT(projectBidStatusHistory[0].pbt.toString());
      }
      if (projectBidStatusHistory[0].oh) {
        setOH(projectBidStatusHistory[0].oh.toString());
      }
      if (projectBidStatusHistory[0].projectMargin) {
        setProjectMargin(projectBidStatusHistory[0].projectMargin.toString());
      }
      if (projectBidStatusHistory[0].financeCost) {
        setFinanceCost(projectBidStatusHistory[0].financeCost.toString());
      }
      if (projectBidStatusHistory[0].depreciation) {
        setDepreciation(projectBidStatusHistory[0].depreciation.toString());
      }
      if (projectBidStatusHistory[0].grossMargin) {
        setGrossMargin(projectBidStatusHistory[0].grossMargin.toString());
      }
      if (projectBidStatusHistory[0].normalContingency) {
        setNormalContingency(
          projectBidStatusHistory[0].normalContingency.toString()
        );
      }
      if (projectBidStatusHistory[0].specialContingency) {
        setSpecialContingency(
          projectBidStatusHistory[0].specialContingency.toString()
        );
      }
    }
  }, [projectBidStatusHistory]);

  const projectBidderDetails = useAppSelector(
    (state) => state.bidderDetails.bidderDetails
  );

  const { loginUserId, roleId } = useAppSelector((state) => {
    return {
      loginUserId: state.user.loggedIn_User.userId,
      roleId: state.user.loggedIn_User.roleId,
    };
  });

  const isReadOnly =
    roleId === 3 || roleId === 4 || roleId === 5 || roleId === 6;
  // project bidder details initial state
  const bidderDetails = useMemo(() => {
    if (isValidArr(projectBidderDetails)) {
      return projectBidderDetails.map(
        ({ rank, bidderName, evaluatedPrice, remarks }) => ({
          rank,
          bidderName,
          evaluatedPrice,
          remarks,
        })
      );
    }
    return [rowfields];
  }, [projectBidderDetails]);

  // To get the current project bid status value
  const projectBidStatus = useMemo(() => {
    if (isValidArr(projectBidStatusHistory)) {
      return projectBidStatusHistory[0].projectOverallWinLossStatus;
    }
    return "";
  }, [projectBidStatusHistory]);

  // To get the project bid manager name
  const projectBidManagerName = useMemo(() => {
    if (
      isValidArr(projectBidStatusHistory) &&
      projectBidStatusHistory[0].bidManagerName
    ) {
      return projectBidStatusHistory[0].bidManagerName;
    }
    return "";
  }, [projectBidStatusHistory]);

  // To get the project works as per new T&E process commenced
  const projectIsNewTEProcessCommenced = useMemo(() => {
    if (
      isValidArr(projectBidStatusHistory) &&
      projectBidStatusHistory[0].isNewTeprocessCommenced !== null
    ) {
      return projectBidStatusHistory[0].isNewTeprocessCommenced ? "yes" : "no";
    }
    return "";
  }, [projectBidStatusHistory]);

  // To get the project works is started or not
  const projectIsWorkStarted = useMemo(() => {
    if (
      isValidArr(projectBidStatusHistory) &&
      projectBidStatusHistory[0].isWorkStarted !== null
    ) {
      return projectBidStatusHistory[0].isWorkStarted ? "yes" : "no";
    }
    return "";
  }, [projectBidStatusHistory]);

  // To get the list of all active project bid status
  const activeBidStatusList = useMemo(() => {
    return projectBidStatusHistory.filter((rec) => rec.isActive === true);
  }, [projectBidStatusHistory]);

  // To prepare object with active bid statuses
  const activeBidStatus = useMemo(() => {
    const bidStatusObj: ObjType = {};
    if (isValidArr(activeBidStatusList)) {
      activeBidStatusList.forEach((val) => {
        bidStatusObj[val.bidStatus] = val.bidStatus;
      });
    }
    return bidStatusObj;
  }, [activeBidStatusList]);

  // To get tender status value
  const statusValues = useMemo(() => {
    const tenderCancelledStatus =
      activeBidStatus[availProjectBidStatus.tenderCancelled] ||
      activeBidStatus[availProjectBidStatus.reTendered];

    return {
      tenderStatus:
        activeBidStatus[availProjectBidStatus.tenderReleased] ||
        activeBidStatus[availProjectBidStatus.tenderAwaited],
      tenderReleasedStatus:
        activeBidStatus[availProjectBidStatus.bidDropped] ||
        activeBidStatus[availProjectBidStatus.bidSubmitted] ||
        tenderCancelledStatus,
      bidSubmittedStatus:
        activeBidStatus[availProjectBidStatus.L1] ||
        activeBidStatus[availProjectBidStatus.lost] ||
        tenderCancelledStatus,
      l1Status:
        activeBidStatus[availProjectBidStatus.LOILOA] ||
        activeBidStatus[availProjectBidStatus.lostL1] ||
        tenderCancelledStatus,
    };
  }, [activeBidStatus]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        projectBidStatus:
          projectStatus === projectBidStatusObj.approved
            ? Yup.string().required()
            : Yup.string().notRequired(),
        tenderMaturity: Yup.string().notRequired(),
        tenderStatus:
          projectStatus === projectBidStatusObj.approved && !projectBidStatus
            ? Yup.string().required("Required")
            : Yup.string().notRequired(),
        tenderReleasedStatus:
          projectStatus === projectBidStatusObj.approved &&
            projectBidStatus === availProjectBidStatus.tenderReleased
            ? Yup.string().required("Required")
            : Yup.string().notRequired(),
        bidSubmittedStatus:
          projectStatus === projectBidStatusObj.approved &&
            projectBidStatus === availProjectBidStatus.bidSubmitted
            ? Yup.string().required("Required")
            : Yup.string().notRequired(),
        l1Status:
          projectStatus === projectBidStatusObj.approved &&
            projectBidStatus === availProjectBidStatus.L1
            ? Yup.string().required("Required")
            : Yup.string().notRequired(),
        date:
          projectStatus === projectBidStatusObj.approved
            ? Yup.date().required("Required")
            : Yup.date().notRequired(),
        statusRemarks:
          projectStatus === projectBidStatusObj.approved
            ? Yup.string().required("Required")
            : Yup.string().notRequired(),
        data:
          projectStatus === projectBidStatusObj.approved
            ? Yup.array().when(["bidSubmittedStatus", "l1Status"], {
              is: (bidSubmittedStatus: string, l1Status: string) => {
                return (
                  bidSubmittedStatus === availProjectBidStatus.lost ||
                  l1Status === availProjectBidStatus.LOILOA ||
                  l1Status === availProjectBidStatus.lostL1
                );
              },
              then: (schema) =>
                schema
                  .of(bidTableValidationSchema)
                  .min(1, "Required")
                  .required("Required"),
            })
            : Yup.array().of(bidTableValidationSchema).notRequired(),
        bidManagerName:
          projectStatus === projectBidStatusObj.approved
            ? Yup.string().when("tenderStatus", {
              is: (tenderStatus: string) => {
                return tenderStatus === availProjectBidStatus.tenderReleased;
              },
              then: (schema) => schema.required("Required"),
            })
            : Yup.string().notRequired(),
        isNewTEProcessCommenced:
          projectStatus === projectBidStatusObj.approved
            ? Yup.string().when("tenderStatus", {
              is: (tenderStatus: string) => {
                return tenderStatus === availProjectBidStatus.tenderReleased;
              },
              then: (schema) => schema.required("Required"),
            })
            : Yup.string().notRequired(),
        isWorkStarted:
          projectStatus === projectBidStatusObj.approved
            ? Yup.string().when("tenderStatus", {
              is: (tenderStatus: string) => {
                return tenderStatus === availProjectBidStatus.tenderReleased;
              },
              then: (schema) => schema.required("Required"),
            })
            : Yup.string().notRequired(),
      }),
    [projectStatus, projectBidStatus]
  );

  type FormProps = Yup.InferType<typeof validationSchema>;

  const initialValues: FormProps = {
    projectBidStatus: projectBidStatus,
    tenderMaturity: tenderMaturity,
    tenderStatus: statusValues.tenderStatus,
    tenderReleasedStatus: statusValues.tenderReleasedStatus,
    bidSubmittedStatus: statusValues.bidSubmittedStatus,
    l1Status: statusValues.l1Status,
    date: undefined,
    statusRemarks: "",
    data: bidderDetails,
    bidManagerName: projectBidManagerName,
    isNewTEProcessCommenced: projectIsNewTEProcessCommenced,
    isWorkStarted: projectIsWorkStarted,
  };

  // To check is admin login
  const isAdminLogin = useMemo(() => {
    if (roleId === 7) {
      return true;
    }
    return false;
  }, [roleId]);

  // To check the bid status exist in active status history or not
  const isBidStatusExist = (status: string) => {
    return activeBidStatusList.some((rec) => rec.bidStatus === status);
  };

  // To check the project closed or not
  const isProjectClosed = useMemo(() => {
    return (
      projectBidStatus === availProjectBidStatus.bidDropped ||
      projectBidStatus === availProjectBidStatus.tenderCancelled ||
      projectBidStatus === availProjectBidStatus.reTendered ||
      projectBidStatus === availProjectBidStatus.lost ||
      projectBidStatus === availProjectBidStatus.LOILOA ||
      projectBidStatus === availProjectBidStatus.lostL1
    );
  }, [projectBidStatus]);

  const onDrop = useCallback((acceptedFiles: FileWithPath[]) => {
    setAttachment(acceptedFiles[0]);
  }, []);

  const handleSubmit = async (values: FormProps) => {
    const winLossFormData: WinLossPostModel = {
      projectId: projectId,
      userId: loginUserId,
      bidStatus: values.projectBidStatus!,
      remarks: values.statusRemarks!,
      date: values.date!,
      isActive: true,
    };

    if (
      values.tenderStatus === availProjectBidStatus.tenderReleased &&
      values.bidManagerName
    ) {
      winLossFormData.bidManagerName = values.bidManagerName;
    }

    if (
      values.tenderStatus === availProjectBidStatus.tenderReleased &&
      values.isNewTEProcessCommenced
    ) {
      winLossFormData.isNewTeprocessCommenced =
        values.isNewTEProcessCommenced === "yes" ? true : false;
    }

    if (
      values.tenderStatus === availProjectBidStatus.tenderReleased &&
      values.isWorkStarted
    ) {
      winLossFormData.isWorkStarted =
        values.isWorkStarted === "yes" ? true : false;
    }

    // if (
    //   pbt &&
    //   oh &&
    //   projectMargin &&
    //   financeCost &&
    //   depreciation &&
    //   grossMargin &&
    //   normalContingency &&
    //   specialContingency
    // ) {
    winLossFormData.pbt = Number(pbt);
    winLossFormData.oh = Number(oh);
    winLossFormData.projectMargin = Number(projectMargin);
    winLossFormData.financeCost = Number(financeCost);
    winLossFormData.depreciation = Number(depreciation);
    winLossFormData.grossMargin = Number(grossMargin);
    winLossFormData.normalContingency = Number(normalContingency);
    winLossFormData.specialContingency = Number(specialContingency);
    // }
    if (projectStatus === projectBidStatusObj.approved) {
      const winLossRes = await WinLossService.postProjectBidStatus(
        winLossFormData
      );
      if (attachment && winLossRes && winLossRes.id) {
        await ProjectService.postProjectWinLossDocument(
          winLossRes.id,
          attachment
        );
        setAttachment(undefined); // Reset attachment on submit
      }
      dispatch(fetchProjectBidStatusHistory(projectId)); // To fetch latest data after submit

      // To fetch the latest project details
      if (isAdminLogin) {
        dispatch(fetchAllProjectsWithStatus());
      } else {
        dispatch(fetchProjectsWithStatusForUserId(loginUserId, roleId));
      }
    }
    if (
      (projectStatus === projectBidStatusObj.rejected ||
        values.projectBidStatus === availProjectBidStatus.bidDropped ||
        values.projectBidStatus === availProjectBidStatus.L1 ||
        values.projectBidStatus === availProjectBidStatus.lost ||
        values.projectBidStatus === availProjectBidStatus.LOILOA ||
        values.projectBidStatus === availProjectBidStatus.lostL1) &&
      isValidArr(values.data) &&
      values.data &&
      values.data[0].rank
    ) {
      const bidderDetailsFormData = values.data!.map((rec) => ({
        id: 0,
        projectId: projectId,
        userId: loginUserId,
        rank: rec.rank as string,
        bidderName: rec.bidderName as string,
        evaluatedPrice: rec.evaluatedPrice as string,
        remarks: rec.remarks as string,
      }));
      await BidderDetailService.postProjectBidderDetails(
        projectId,
        bidderDetailsFormData
      );
      dispatch(fetchProjectBidderDetails(projectId)); // To fetch latest bidder details after submit
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      enableReinitialize={true}
    >
      {(formik) => {
        return (
          <>
            <Form>
              <Grid container spacing={2}>
                {projectStatus === projectBidStatusObj.approved && (
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="autocomplete"
                      name="tenderMaturity"
                      options={tenderMaturityOptions}
                      placeholder="Select Tender Maturity"
                      label="Tender Maturity"
                      disabled={true}
                      required={true}
                      size="small"
                    />
                  </Grid>
                )}

                {projectStatus === projectBidStatusObj.approved && (
                  <Grid item xs={12} sm={6} md={4}>
                    <FormikControl
                      control="autocomplete"
                      options={tenderOptions}
                      name="tenderStatus"
                      placeholder="Select Status"
                      label="Tender Status"
                      required={true}
                      size="small"
                      updateBidStatus={true}
                      disabled={
                        isReadOnly ||
                        (isAdminLogin
                          ? false
                          : isBidStatusExist(
                            availProjectBidStatus.tenderReleased
                          ))
                      }
                    />
                  </Grid>
                )}
                {formik.values.tenderStatus ===
                  availProjectBidStatus.tenderReleased ? (
                  <>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikControl
                        control="input"
                        name="bidManagerName"
                        label="Bid Manager Name"
                        placeholder="Bid Manager"
                        size="small"
                        required={true}
                        disabled={
                          isReadOnly ||
                          (isAdminLogin
                            ? false
                            : projectBidManagerName
                              ? true
                              : false)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikControl
                        control="autocomplete"
                        name="isNewTEProcessCommenced"
                        options={yesOrNoStatusOptions}
                        label="Work as per new T&E process commenced?"
                        placeholder="Select"
                        size="small"
                        required={true}
                        disabled={
                          isReadOnly ||
                          (isAdminLogin
                            ? false
                            : projectIsNewTEProcessCommenced
                              ? true
                              : false)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikControl
                        control="autocomplete"
                        name="isWorkStarted"
                        options={yesOrNoStatusOptions}
                        label="Work Started"
                        placeholder="Select"
                        size="small"
                        required={true}
                        disabled={
                          isReadOnly ||
                          (isAdminLogin
                            ? false
                            : projectIsWorkStarted
                              ? true
                              : false)
                        }
                      />
                    </Grid>
                  </>
                ) : null}
                {formik.values.tenderStatus ===
                  availProjectBidStatus.tenderReleased &&
                  isBidStatusExist(availProjectBidStatus.tenderReleased) && (
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikControl
                        control="autocomplete"
                        options={tenderReleasedOptions}
                        name="tenderReleasedStatus"
                        placeholder="Select Status"
                        label="Tender Released Status"
                        required={true}
                        size="small"
                        updateBidStatus={true}
                        disabled={
                          isReadOnly ||
                          (isAdminLogin
                            ? false
                            : isBidStatusExist(
                              availProjectBidStatus.bidSubmitted
                            ) || isProjectClosed)
                        }
                      />
                    </Grid>
                  )}
                {formik.values.tenderStatus ===
                  availProjectBidStatus.tenderReleased &&
                  formik.values.tenderReleasedStatus ===
                  availProjectBidStatus.bidSubmitted &&
                  isBidStatusExist(availProjectBidStatus.bidSubmitted) && (
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikControl
                        control="autocomplete"
                        options={bidSubmittedOptions}
                        name="bidSubmittedStatus"
                        placeholder="Select Status"
                        label="Bid Submitted Status"
                        required={true}
                        size="small"
                        info="Please enter only if 100% sure"
                        updateBidStatus={true}
                        disabled={
                          isReadOnly ||
                          (isAdminLogin
                            ? false
                            : isBidStatusExist(availProjectBidStatus.L1) ||
                            isProjectClosed)
                        }
                      />
                    </Grid>
                  )}
                {formik.values.tenderStatus ===
                  availProjectBidStatus.tenderReleased &&
                  formik.values.tenderReleasedStatus ===
                  availProjectBidStatus.bidSubmitted &&
                  formik.values.bidSubmittedStatus ===
                  availProjectBidStatus.L1 &&
                  isBidStatusExist(availProjectBidStatus.L1) && (
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikControl
                        control="autocomplete"
                        options={l1Options}
                        name="l1Status"
                        placeholder="Select Status"
                        label="L1 Status"
                        required={true}
                        size="small"
                        info="Please enter only if 100% sure"
                        updateBidStatus={true}
                        disabled={
                          isReadOnly || (isAdminLogin ? false : isProjectClosed)
                        }
                      />
                    </Grid>
                  )}
                {projectStatus === projectBidStatusObj.approved &&
                  (isAdminLogin || !isProjectClosed) && (
                    <Grid item xs={12} sm={6} md={4}>
                      <FormikControl
                        control="datepicker"
                        name="date"
                        label="Date"
                        placeholderText="dd/MM/YYYY"
                        required={true}
                        disabled={isReadOnly}
                      />
                    </Grid>
                  )}
              </Grid>
              {projectStatus === projectBidStatusObj.approved &&
                (isAdminLogin || !isProjectClosed) && (
                  <>
                    <Box mt={2}>
                      <FormikControl
                        control="input"
                        name="statusRemarks"
                        label="Remarks"
                        required={true}
                        multiline
                        rows={2}
                        size="small"
                        disabled={isReadOnly}
                      />
                    </Box>
                    <Box my={2}>
                      <DropBox
                        accept={{
                          "image/*": [".jpeg", ".jpg", ".png"],
                          "application/pdf": [".pdf"],
                        }}
                        onDrop={onDrop}
                        allowedFileTypes=".jpeg, .jpg, .png and .pdf"
                        className="winloss-dropzone"
                      />
                      <Typography variant="subtitle2">
                        {attachment?.path}
                      </Typography>
                    </Box>
                  </>
                )}
              {(formik.values.bidSubmittedStatus ===
                availProjectBidStatus.lost ||
                formik.values.l1Status === availProjectBidStatus.LOILOA ||
                formik.values.l1Status === availProjectBidStatus.lostL1) && (
                  <Grid
                    container
                    spacing={2}
                    alignItems="start"
                    style={{ marginTop: "2px" }}
                  >
                    <Grid item xs={12} sm={6} md={4}>
                      <TextBox
                        label="Project Margin (i.e. PBT excl OH and Contingency) (INR Cr)"
                        size="small"
                        fullWidth
                        required={pbt === "0" ? false : true}
                        value={pbt}
                        type="number"
                        changeHandler={handlePBTChange}
                        disabled={
                          isReadOnly ||
                          (isAdminLogin
                            ? false
                            : projectBidStatusHistory[0]?.pbt
                              ? true
                              : false)
                        }
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={4}>
                      <TextBox
                        label="OH (Corp + SBU OH) (INR Cr)"
                        size="small"
                        fullWidth
                        value={oh}
                        type="number"
                        required={oh === "0" ? false : true}
                        changeHandler={handleOHChange}
                        disabled={
                          isReadOnly ||
                          (isAdminLogin
                            ? false
                            : projectBidStatusHistory[0]?.oh
                              ? true
                              : false)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextBox
                        label="Project Margin (INR Cr)"
                        value={projectMargin}
                        type="number"
                        required={projectMargin === "0" ? false : true}
                        size="small"
                        fullWidth
                        changeHandler={handleProjectMarginChange}
                        disabled={
                          isReadOnly ||
                          (isAdminLogin
                            ? false
                            : projectBidStatusHistory[0]?.projectMargin
                              ? true
                              : false)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextBox
                        label="Finance cost (INR Cr)"
                        size="small"
                        fullWidth
                        required={financeCost === "0" ? false : true}
                        value={financeCost}
                        type="number"
                        changeHandler={handleFinanceCostChange}
                        disabled={
                          isReadOnly ||
                          (isAdminLogin
                            ? false
                            : projectBidStatusHistory[0]?.financeCost
                              ? true
                              : false)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextBox
                        label="Other contingency (INR Cr)"
                        size="small"
                        fullWidth
                        required={depreciation === "0" ? false : true}
                        value={depreciation}
                        type="number"
                        changeHandler={handleDepreciationChange}
                        disabled={
                          isReadOnly ||
                          (isAdminLogin
                            ? false
                            : projectBidStatusHistory[0]?.depreciation
                              ? true
                              : false)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextBox
                        label="Escalation (INR Cr)"
                        required={grossMargin === "0" ? false : true}
                        value={grossMargin}
                        type="number"
                        disabled={
                          isReadOnly ||
                          (isAdminLogin
                            ? false
                            : projectBidStatusHistory[0]?.grossMargin
                              ? true
                              : false)
                        }
                        size="small"
                        fullWidth
                        changeHandler={handleGrossMarginChange}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextBox
                        label="Normal contingency (INR Cr)"
                        size="small"
                        fullWidth
                        required={normalContingency === "0" ? false : true}
                        value={normalContingency}
                        type="number"
                        changeHandler={handleNormalContingencyChange}
                        disabled={
                          isReadOnly ||
                          (isAdminLogin
                            ? false
                            : projectBidStatusHistory[0]?.normalContingency
                              ? true
                              : false)
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextBox
                        label="Special contingency (INR Cr)"
                        size="small"
                        fullWidth
                        required={specialContingency === "0" ? false : true}
                        value={specialContingency}
                        type="number"
                        changeHandler={handleSpecialContingencyChange}
                        disabled={
                          isReadOnly ||
                          (isAdminLogin
                            ? false
                            : projectBidStatusHistory[0]?.specialContingency
                              ? true
                              : false)
                        }
                      />
                    </Grid>
                  </Grid>
                )}

              {(projectStatus === projectBidStatusObj.rejected ||
                (formik.values.tenderStatus ===
                  availProjectBidStatus.tenderReleased &&
                  (formik.values.tenderReleasedStatus ===
                    availProjectBidStatus.bidDropped ||
                    (formik.values.tenderReleasedStatus ===
                      availProjectBidStatus.bidSubmitted &&
                      ((formik.values.bidSubmittedStatus ===
                        availProjectBidStatus.L1 &&
                        (!formik.values.l1Status ||
                          formik.values.l1Status ===
                          availProjectBidStatus.LOILOA ||
                          formik.values.l1Status ===
                          availProjectBidStatus.lostL1)) ||
                        formik.values.bidSubmittedStatus ===
                        availProjectBidStatus.lost))))) &&
                (isAdminLogin ||
                  !(isProjectClosed && !isValidArr(projectBidderDetails))) && (
                  <BidWinLossTable
                    data={formik.values.data}
                    formik={formik}
                    isProjectClosed={
                      isReadOnly ||
                      (isAdminLogin
                        ? false
                        : isProjectClosed ||
                        (projectStatus === projectBidStatusObj.rejected &&
                          isValidArr(projectBidderDetails)))
                    }
                  />
                )}
              {(isAdminLogin ||
                (projectStatus === projectBidStatusObj.rejected &&
                  !isValidArr(projectBidderDetails)) ||
                (projectStatus === projectBidStatusObj.approved &&
                  !isProjectClosed)) && (
                  <Stack>
                    <Button
                      type="submit"
                      variant="contained"
                      sx={{ alignSelf: "flex-end", margin: "1rem" }}
                      disabled={isReadOnly || formik.isSubmitting}
                    >
                      Submit
                    </Button>
                  </Stack>
                )}
            </Form>
            {formik.isSubmitting && <Loader />}
          </>
        );
      }}
    </Formik>
  );
};

export default BidStatusSection;
